import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, MapPin, ArrowUpRight, Sparkles, Award, ExternalLink, Radio } from "lucide-react";
import { EXPERIENCES } from "./data";
import { useContent } from "./useContent";

const Experience = () => {
  const items = useContent("experiences", EXPERIENCES);
  const [active, setActive] = React.useState(items[0]?.id || EXPERIENCES[0].id);
  const current = items.find((e) => e.id === active) || items[0] || EXPERIENCES[0];

  React.useEffect(() => {
    if (items.length && !items.find((e) => e.id === active)) {
      setActive(items[0].id);
    }
  }, [items, active]);

  return (
    <section id="experience" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">06 / Experience</div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Companies that <span className="italic">taught me</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Six organisations, six very different rooms. From a current international distribution role at FYSE Ventures in Italy back through internships in operations, hospitality, trade, and infrastructure — every chapter a lesson in how real businesses actually breathe.
            </p>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-10">
          {/* Tabs */}
          <div className="space-y-2">
            {EXPERIENCES.map((e, i) => (
              <button
                key={e.id}
                onClick={() => setActive(e.id)}
                className={`w-full text-left group relative rounded-2xl border p-5 transition-all ${
                  active === e.id
                    ? "border-[#e6c769]/50 bg-[#e6c769]/[0.06]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/30"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${
                    active === e.id ? "bg-[#e6c769] text-black" : "bg-white/5 text-white/60 border border-white/10"
                  }`}>
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono flex items-center gap-2">
                      <span>{String(i + 1).padStart(2, "0")} · {e.span}</span>
                      {e.current && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300/90 tracking-[0.2em] text-[8.5px]">
                          <span className="relative flex w-1.5 h-1.5">
                            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          </span>
                          Current
                        </span>
                      )}
                    </div>
                    <div className={`mt-1 text-[16px] truncate ${active === e.id ? "text-white" : "text-white/80"}`}>
                      {e.company}
                    </div>
                    <div className="text-[12px] text-white/50 truncate">{e.role}</div>
                  </div>
                  <ArrowUpRight className={`w-4 h-4 mt-1 shrink-0 transition-all ${
                    active === e.id ? "text-[#e6c769] -translate-y-0.5 translate-x-0.5" : "text-white/30"
                  }`} />
                </div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="sticky top-28 self-start">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[24px] border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl overflow-hidden"
              >
                <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[#3b82f6]/10 blur-3xl" />
                <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-[#e6c769]/10 blur-3xl" />
                <div className="relative p-8 md:p-12">
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                      <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769] font-mono flex items-center gap-2">
                        <span>{current.span} · {current.role}</span>
                        {current.current && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300/90 tracking-[0.22em] text-[9px] normal-case">
                            <span className="relative flex w-1.5 h-1.5">
                              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                              <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            </span>
                            CURRENT
                          </span>
                        )}
                      </div>
                      <h3 className="mt-2 text-[30px] md:text-[44px] leading-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {current.company}
                      </h3>
                      <div className="mt-2 text-[13px] text-white/55 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {current.location}</span>
                        {current.website && (
                          <a
                            href={current.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`experience-website-${current.id}`}
                            className="inline-flex items-center gap-1.5 text-[#e6c769]/90 hover:text-[#e6c769] transition-colors font-mono tracking-wide"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            {current.websiteDisplay || current.website}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="mt-6 text-[15px] md:text-[16px] leading-[1.75] text-white/65 max-w-2xl">
                    {current.summary}
                  </p>

                  <div className="mt-8 grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-3">Skills earned</div>
                      <div className="flex flex-wrap gap-2">
                        {current.skills.map((s) => (
                          <span key={s} className="text-[12px] px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-white/70">{s}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-3">What I shipped</div>
                      <ul className="space-y-2">
                        {current.achievements.map((a) => (
                          <li key={a} className="flex items-start gap-2 text-[13px] text-white/65">
                            <Sparkles className="w-3.5 h-3.5 mt-1 text-[#e6c769] shrink-0" /> {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
